import { z } from 'zod';

import { CHAT_PREVIEW_MAX, ChatTopicKindSchema } from './chat.dto';

/**
 * Unified chat list (`GET /api/me/chats`) — the Telegram-style inbox: one row
 * per direct-message partner and one row per team chat (a "forum" whose
 * channels open as a second level).  Sorted server-side: pinned rows first
 * (newest pin on top), then by last activity.
 *
 * Privacy: every field is read as the caller under RLS. A team row's preview,
 * unread count and mention flag aggregate only the channels the caller may
 * read — a restricted project's channel never contributes for a member
 * without project access (migration 0038).
 */

export const ChatListKindSchema = z.enum(['dm', 'team']);
export type ChatListKind = z.infer<typeof ChatListKindSchema>;

export const ChatListFilterSchema = z.enum(['all', 'unread', 'direct', 'teams']);
export type ChatListFilter = z.infer<typeof ChatListFilterSchema>;

/** The newest message a row shows ("Anna: …", "#Website · Anna: …"). */
export const ChatListLastMessageSchema = z.object({
  id: z.string().uuid(),
  /** Plain-text excerpt (`toChatPreview`); null for an attachment-only message. */
  preview: z.string().max(CHAT_PREVIEW_MAX).nullable(),
  authorId: z.string().uuid(),
  authorName: z.string().nullable(),
  at: z.string().datetime(),
  /** Team rows: the channel the message was posted in. Null on direct messages. */
  topicId: z.string().uuid().nullable(),
  topicName: z.string().nullable(),
  topicKind: ChatTopicKindSchema.nullable(),
});
export type ChatListLastMessage = z.infer<typeof ChatListLastMessageSchema>;

export const ChatListPartnerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  username: z.string(),
  avatarUrl: z.string().nullable(),
  avatarColor: z.string().nullable(),
});
export type ChatListPartner = z.infer<typeof ChatListPartnerSchema>;

export const ChatListRowSchema = z.object({
  kind: ChatListKindSchema,
  /** dm → the thread id; team → the team id. */
  id: z.string().uuid(),
  /** Partner name / team name. */
  name: z.string(),
  avatarUrl: z.string().nullable(),
  /** Direct messages only. */
  partner: ChatListPartnerSchema.nullable(),
  /** Team rows only (links: `/my/messages?team=<slug>`). */
  teamSlug: z.string().nullable(),
  lastMessage: ChatListLastMessageSchema.nullable(),
  /** Sort key: the newest visible message, else when the chat was created. */
  activityAt: z.string().datetime(),
  /**
   * Unread messages from others. Team rows: the sum over the team's readable,
   * non-archived channels EXCLUDING channels I muted (Telegram forum rule).
   */
  unreadCount: z.number().int().nonnegative(),
  /** An unread message @mentions me (any readable channel, muted ones included). */
  hasMention: z.boolean(),
  /** I muted the whole chat: the badge turns grey, the row stays in place. */
  isMuted: z.boolean(),
  isPinned: z.boolean(),
  pinnedAt: z.string().datetime().nullable(),
  /** Team rows: my muted channels (lets a live message skip the unread bump). */
  mutedTopicIds: z.array(z.string().uuid()),
  /** Direct messages: my read marker (the "New messages" divider on open). */
  lastReadAt: z.string().datetime().nullable(),
});
export type ChatListRow = z.infer<typeof ChatListRowSchema>;

export const ChatListPageSchema = z.object({
  items: z.array(ChatListRowSchema),
  /** Opaque cursor for the next page; null at the end. Pinned rows all come on page one. */
  nextCursor: z.string().nullable(),
});
export type ChatListPage = z.infer<typeof ChatListPageSchema>;

export const ChatListQuerySchema = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  filter: ChatListFilterSchema.default('all'),
  /** Name filter (partner name / @username / team name), case-insensitive. */
  q: z.string().trim().max(100).optional(),
});
export type ChatListQuery = z.infer<typeof ChatListQuerySchema>;

/** At most this many pinned chats per person (Telegram keeps it small too). */
export const CHAT_PIN_LIMIT = 10;

export const UpdateChatListPrefsSchema = z
  .object({
    pinned: z.boolean().optional(),
    muted: z.boolean().optional(),
  })
  .refine((v) => v.pinned !== undefined || v.muted !== undefined, {
    message: 'pinned or muted is required',
  });
export type UpdateChatListPrefsInput = z.infer<typeof UpdateChatListPrefsSchema>;

export const ChatListPrefsSchema = z.object({
  kind: ChatListKindSchema,
  id: z.string().uuid(),
  isPinned: z.boolean(),
  pinnedAt: z.string().datetime().nullable(),
  isMuted: z.boolean(),
});
export type ChatListPrefsProjection = z.infer<typeof ChatListPrefsSchema>;
